/**
 * Date helpers.
 *
 * Ported from v1 with its bugs already fixed — see the notes below, both of
 * which were live faults that shipped and had to be found the hard way.
 */

/**
 * Built from local calendar parts, never toISOString().
 *
 * setHours() works in local time but toISOString() reports UTC, so during BST
 * (UTC+1) local midnight is 23:00 the previous day. v1 did exactly that and
 * silently returned yesterday's date all summer; on a Monday it dragged the
 * whole forecast window back a full week.
 */
export const isoDate = (d) => {
  const x = new Date(d);
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, "0");
  const day = String(x.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export const todayStr = () => isoDate(new Date());

export const parseDate = (s) => {
  if (!s) return null;
  if (s instanceof Date) return new Date(s);
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s));
  // Construct from parts so a plain YYYY-MM-DD is never parsed as UTC midnight
  // and then shifted a day by the local offset.
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(s);
  if (isNaN(d)) return null;
  d.setHours(0, 0, 0, 0);
  return d;
};

export const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
export const addWeeks = (d, n) => addDays(d, n * 7);
export const addMonths = (d, n) => {
  const x = new Date(d);
  x.setMonth(x.getMonth() + n);
  return x;
};
export const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
export const lastDayOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);

export const startOfWeekMon = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x;
};

/** DST-safe day index. */
export const epochDay = (d) =>
  Math.floor(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 86400000);

/** Whole days from `iso` to `todayIso`. Positive = overdue by that many days. */
export function daysLate(iso, todayIso) {
  const d = parseDate(iso);
  const t = parseDate(todayIso);
  if (!d || !t) return null;
  return epochDay(t) - epochDay(d);
}

/** The Friday on or after `d`. Clients pay on Fridays, so that's the landing day. */
export function fridayOnOrAfter(d) {
  const x = new Date(d);
  x.setDate(x.getDate() + ((5 - x.getDay() + 7) % 7));
  return x;
}

/**
 * When a document is really expected to settle.
 *
 * Default is the Friday on or after its due date. If that Friday has already
 * passed and the document is still open, the money plainly didn't arrive, so it
 * rolls to the next Friday from today rather than forecasting cash into the
 * past — most of the open book is overdue, and without this it would all land
 * in history where it does no good.
 *
 * A manual override always wins. The rule is a starting point, not a claim to
 * know when a year-late bill will clear.
 */
export function expectedPaymentDate(dueDateIso, override, todayIso, { assumeOverdue = false } = {}) {
  if (override) return override;
  const due = parseDate(dueDateIso);
  if (!due) return null;
  const onTime = isoDate(fridayOnOrAfter(due));
  if (onTime >= todayIso) return onTime;
  // Past its due date and nobody has said when it will actually move.
  //
  // This used to answer "the next payment run", which is a guess wearing the
  // clothes of a fact: £196,000 of invoices overdue since May were forecast to
  // arrive on the same Friday, and the near weeks were fiction as a result.
  // The honest answer is that we do not know, and a forecast is better for
  // saying so — the amount is counted and shown as unscheduled instead.
  return assumeOverdue ? isoDate(fridayOnOrAfter(parseDate(todayIso))) : null;
}

export const fmtMoney = (n, currency = "GBP") => {
  const v = Number(n) || 0;
  try {
    return v.toLocaleString("en-GB", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    });
  } catch {
    return `${currency} ${Math.round(v).toLocaleString("en-GB")}`;
  }
};

export const fmtDay = (iso) => {
  const d = parseDate(iso);
  if (!d) return "—";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

export const fmtWeek = (d) =>
  d.toLocaleDateString("en-GB", { day: "numeric", month: "short" }).toUpperCase();

// Syrup's financial year starts 1 July. Week numbers in the grid are FY weeks,
// matching v1 — "W10" means the tenth week of this financial year, which is the
// number people already use when talking about a week.
const FY_START_MONTH = 6; // 0-indexed: July
const FY_START_DAY = 1;

export function fyWeek(d) {
  const y = d.getFullYear();
  const fy =
    d.getMonth() > FY_START_MONTH || (d.getMonth() === FY_START_MONTH && d.getDate() >= FY_START_DAY)
      ? y + 1
      : y;
  const anchor = startOfWeekMon(new Date(fy - 1, FY_START_MONTH, FY_START_DAY));
  const weekStart = startOfWeekMon(d);
  return {
    fy: fy % 100,
    week: Math.floor(Math.round((weekStart - anchor) / 86400000) / 7) + 1,
  };
}

/**
 * The next working day, if the given date falls on a weekend.
 *
 * Direct debits aren't collected on a Saturday or Sunday — they go the
 * following Monday, which is a real difference to a weekly cash forecast when a
 * collection sits on the boundary between two weeks.
 *
 * Weekends only. England & Wales bank holidays would shift a collection too,
 * but they need a maintained list, and getting one wrong is worse than not
 * claiming to handle them.
 */
export function nextWorkingDay(d) {
  const x = new Date(d);
  const day = x.getDay();
  if (day === 6) x.setDate(x.getDate() + 2); // Saturday -> Monday
  else if (day === 0) x.setDate(x.getDate() + 1); // Sunday -> Monday
  return x;
}

/**
 * The last Friday of a month — how payroll is actually dated.
 *
 * Not the last working day, and not a fixed date: salaries land on a Friday,
 * and which date that is moves every month.
 */
export function lastFridayOfMonth(year, monthIndex) {
  const d = new Date(year, monthIndex + 1, 0); // last day of the month
  while (d.getDay() !== 5) d.setDate(d.getDate() - 1);
  return d;
}
